import clsx from "clsx";
import { LicensePlateInput } from "@/components/bookings/LicensePlateInput";
import {
  formatLicensePlateForDisplay,
  normalizeLicensePlateForStorage,
} from "@/lib/licensePlate";
import { VEHICLE_TYPES } from "@/lib/vehicle";

const inputClass = "eh-input";

export interface VehicleFormValues {
  license_plate: string;
  license_plate_country: string;
  vehicle_type: string;
  vehicle_length_m: string;
  vehicle_width_m: string;
  vehicle_height_m: string;
  vehicle_weight_kg: string;
  tent_size: string;
}

export const EMPTY_VEHICLE_FORM: VehicleFormValues = {
  license_plate: "",
  license_plate_country: "DE",
  vehicle_type: "",
  vehicle_length_m: "",
  vehicle_width_m: "",
  vehicle_height_m: "",
  vehicle_weight_kg: "",
  tent_size: "",
};

export interface VehicleFieldsProps {
  values: VehicleFormValues;
  onChange: (values: VehicleFormValues) => void;
  disabled?: boolean;
  className?: string;
  showVehicleLength?: boolean;
  vehicleLengthRequired?: boolean;
}

export function VehicleFields({
  values,
  onChange,
  disabled,
  className,
  showVehicleLength = false,
  vehicleLengthRequired = false,
}: VehicleFieldsProps) {
  return (
    <div className={clsx("grid gap-3 sm:grid-cols-2", className)}>
      <label className="text-sm sm:col-span-2">
        <span className="mb-1 block text-label-secondary">Kennzeichen</span>
        <LicensePlateInput
          className={inputClass}
          value={values.license_plate}
          onChange={(license_plate) => onChange({ ...values, license_plate })}
          disabled={disabled}
        />
      </label>
      <label className="text-sm">
        <span className="mb-1 block text-label-secondary">Land (Plakette)</span>
        <input
          className={inputClass}
          value={values.license_plate_country}
          onChange={(e) =>
            onChange({ ...values, license_plate_country: e.target.value.toUpperCase().slice(0, 2) })
          }
          placeholder="DE"
          maxLength={2}
          disabled={disabled}
        />
      </label>
      <label className="text-sm">
        <span className="mb-1 block text-label-secondary">Fahrzeugtyp</span>
        <select
          className="eh-select-input"
          value={values.vehicle_type}
          onChange={(e) => onChange({ ...values, vehicle_type: e.target.value })}
          disabled={disabled}
        >
          <option value="">— Keine Angabe —</option>
          {VEHICLE_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </label>
      {showVehicleLength ? (
        <label className="text-sm sm:col-span-2">
          <span className="mb-1 block text-label-secondary">
            Fahrzeuglänge (m){vehicleLengthRequired ? " *" : ""}
          </span>
          <input
            className={inputClass}
            value={values.vehicle_length_m}
            onChange={(e) => onChange({ ...values, vehicle_length_m: e.target.value })}
            placeholder="z. B. 7,5"
            inputMode="decimal"
            disabled={disabled}
            required={vehicleLengthRequired}
          />
        </label>
      ) : null}
    </div>
  );
}

export function vehicleFieldsToApi(values: VehicleFormValues): Record<string, string | number | null> {
  const plate = normalizeLicensePlateForStorage(values.license_plate);
  const country = values.license_plate_country.trim();
  const vehicle = values.vehicle_type.trim();
  const parseDim = (raw: string, max: number) => {
    const normalized = raw.trim().replace(",", ".");
    const num = normalized ? Number.parseFloat(normalized) : Number.NaN;
    return Number.isFinite(num) && num > 0 && num <= max ? num : null;
  };
  return {
    license_plate: plate || null,
    license_plate_country: plate && country ? country.toUpperCase() : null,
    vehicle_type: vehicle || null,
    vehicle_length_m: parseDim(values.vehicle_length_m, 25),
    vehicle_width_m: parseDim(values.vehicle_width_m, 25),
    vehicle_height_m: parseDim(values.vehicle_height_m, 25),
    vehicle_weight_kg: parseDim(values.vehicle_weight_kg, 50000),
    tent_size: values.tent_size.trim() || null,
  };
}

function dimToFormValue(value: string | number | null | undefined): string {
  return value != null && value !== "" ? String(value) : "";
}

export function vehicleFieldsFromBooking(booking: {
  license_plate?: string | null;
  license_plate_country?: string | null;
  vehicle_type?: string | null;
  vehicle_length_m?: string | number | null;
  vehicle_width_m?: string | number | null;
  vehicle_height_m?: string | number | null;
  vehicle_weight_kg?: string | number | null;
  tent_size?: string | null;
}): VehicleFormValues {
  return {
    license_plate: formatLicensePlateForDisplay(booking.license_plate ?? ""),
    license_plate_country: booking.license_plate_country ?? "DE",
    vehicle_type: booking.vehicle_type ?? "",
    vehicle_length_m: dimToFormValue(booking.vehicle_length_m),
    vehicle_width_m: dimToFormValue(booking.vehicle_width_m),
    vehicle_height_m: dimToFormValue(booking.vehicle_height_m),
    vehicle_weight_kg: dimToFormValue(booking.vehicle_weight_kg),
    tent_size: booking.tent_size ?? "",
  };
}
